import { REPORT_CHART_FEEDBACK_BASED_MEASURES } from '@vision/ui/constants';
import { ReportChartFeedbackBaseType } from '@vision/ui/enums';
import { ReportChartConfiguration, ReportChartFilterItemTypes, ReportChartMeasureType } from '@vision/ui/interfaces';
import { array, lazy, string } from 'yup';
import { BarChart3ChartValueComponent, BarChart3CompareRenderer } from '../../components';
import {
  DefaultReportChartThemeFont,
  DefaultReportChartThemeSeries,
  extendDefaultChartValidationSchema,
} from '../default-chart.config';
import { BarChart3Filtres } from './bar-chart3.filtre';

const VALIDATION_SCHEMA = {
  selectedLineMeasure: string().required('validation:rules.reports.lineMeasure.required'),
  selectedBarMeasures: array()
    .of(string().required('validation:rules.generic.required'))
    .min(1, 'validation:rules.generic.required'),
  selectedFeedbackBasedQuestionBased: lazy(() =>
    string().when(['selectedLineMeasure', 'selectedBarMeasures'], {
      is: (selectedLineMeasure: ReportChartMeasureType, selectedBarMeasures: ReportChartMeasureType[]) =>
        REPORT_CHART_FEEDBACK_BASED_MEASURES.includes(selectedLineMeasure) ||
        (selectedBarMeasures ?? []).some((measure) => REPORT_CHART_FEEDBACK_BASED_MEASURES.includes(measure)),
      then: (selectedFeedbackBasedQuestionBased) => selectedFeedbackBasedQuestionBased.required(),
      otherwise: (selectedFeedbackBasedQuestionBased) => selectedFeedbackBasedQuestionBased.notRequired(),
    }),
  ),
  selectedCompareFlowIds: lazy(() =>
    array().when(['selectedFeedbackBasedQuestionBased'], {
      is: (selectedFeedbackBasedQuestionBased: ReportChartFeedbackBaseType) =>
        selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED,
      then: (selectedCompareFlowIds) => selectedCompareFlowIds.required().min(1),
      otherwise: (selectedCompareFlowIds) => selectedCompareFlowIds.notRequired(),
    }),
  ),
  selectedCompareQuestionIds: lazy(() =>
    array().when(['selectedFeedbackBasedQuestionBased'], {
      is: (selectedFeedbackBasedQuestionBased: ReportChartFeedbackBaseType) =>
        selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED,
      then: (selectedCompareQuestionIds) => selectedCompareQuestionIds.required().min(1),
      otherwise: (selectedCompareQuestionIds) => selectedCompareQuestionIds.notRequired(),
    }),
  ),
};

export const BarChart3DefaultConfiguration: ReportChartConfiguration = {
  supportsLegendSettings: true,
  validationSchema: extendDefaultChartValidationSchema({
    params: VALIDATION_SCHEMA,
    withCompareCriteria: true,
  }),
  measures: null,
  defaultMeasure: 'cx-metrics',
  compareRendererComponent: BarChart3CompareRenderer,
  lineMeasure: null,
  barMeasure: [],
  chartValueComponent: BarChart3ChartValueComponent,
  hideCompare: true,
  defaultFeedbackBasedQuestionBased: ReportChartFeedbackBaseType.FEEDBACK_BASED,
  customize: {
    active: (values) => values.selectedMeasure && values.selectedCompare.length > 0,
    transactionalDate: {
      active: false,
    },
    feedbackCount: {
      active: false,
    },
    limits: {
      upperLimitActive: false,
      upperLimit: 0,
      lowerLimitActive: false,
      lowerLimit: 0,
    },
    sortResults: {
      limit: 0,
      order: null,
    },
  },
  filters: (measureType: ReportChartMeasureType) => {
    for (const item of BarChart3Filtres) {
      if (item.measures.includes(measureType)) {
        return item.filterItems;
      }
    }
    return ReportChartFilterItemTypes;
  },
  defaultTheme: {
    active: (values) => values.selectedMeasure && values.selectedCompare.length > 0,
    title: {
      ...DefaultReportChartThemeFont,
      label: '',
    },
    xAxis: {
      ...DefaultReportChartThemeFont,
    },
    yAxis: {
      ...DefaultReportChartThemeFont,
      activeDisplayFormat: true,
      displayFormat: 'auto',
      minMaxValue: {
        minValue: null,
        maxValue: null,
      },
    },
    dataLabels: {
      ...DefaultReportChartThemeFont,
      active: false,
      alignment: 'horizontal',
      displayFormat: 'auto',
      roundingMethod: 'default',
      labelDisplayFormat: 'number',
      position: 'outside',
    },
    serieDot: {
      active: false,
      symbolSize: 6,
      symbolType: 'circle',
      symbolFilling: 'full',
    },
    series: {
      ...DefaultReportChartThemeSeries,
    },
  },
};
